import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { formatDistance, formatDuration } from '../../utils/formatters';
import { CurvinessBadge } from '../common/CurvinessBadge';
import { ElevationGraph } from '../common/ElevationGraph';
import { RoadBreakdown } from '../common/RoadBreakdown';
import { ProfileLinks } from '../common/ProfileLinks';
import { BusinessOrganizerCard } from '../common/BusinessOrganizerCard';
import { MediaCarousel } from '../common/MediaCarousel';
import { EmbedBuilder } from '../Embed/EmbedBuilder';
import { getProfile } from '../../services/profileService';
import { fetchElevationSegments } from '../../utils/elevation';

// Lazy to break the route-detail ↔ user-profile import cycle.
const UserProfilePanel = lazy(() => import('../Profile/UserProfilePanel').then(m => ({ default: m.UserProfilePanel })));
import { unpublishRoute, rateRoute, getUserRating, getUserStats } from '../../services/publishService';
import { ensureRouteBriefing } from '../../services/aiService';
import { buildShareURL, copyToClipboard } from '../../utils/share';
import { downloadGPX } from '../../utils/gpx';
import { generateRouteCard } from '../../utils/shareCard';
import './RouteDetailModal.css';

function youTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('?')[0];
    if (u.hostname.includes('youtube.com')) {
      if (u.searchParams.get('v')) return u.searchParams.get('v');
    }
  } catch {}
  return null;
}

const STOP_COLORS = ['#10b981', '#2563eb', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#f97316'];
const STOP_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function RouteDetailModal({ route, onClose, onUnpublished, onExploreUser, zIndex = 1400 }) {
  const { dispatch, showToast } = useRoute();
  const { user, isSubscribed } = useAuth();
  const [profileTarget, setProfileTarget] = useState(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const overlayRef = useRef(null);
  const [userRating, setUserRating] = useState(null);
  const [hoverStar, setHoverStar] = useState(0);
  const [isRating, setIsRating] = useState(false);
  const [avgRating, setAvgRating] = useState(route.averageRating ?? null);
  const [ratingCount, setRatingCount] = useState(route.ratingCount ?? 0);
  const [userStats, setUserStats] = useState(null);
  const [elevation, setElevation] = useState(null);
  const [authorProfile, setAuthorProfile] = useState(null);
  const [showEmbed, setShowEmbed] = useState(false);
  const [briefing, setBriefing] = useState(route.aiBriefing || null);
  const isOwn = user?.uid === route.userId;

  useEffect(() => {
    getProfile(route.userId).then(setAuthorProfile).catch(() => {});
  }, [route.userId]);

  // Ride briefing: cached on the doc; lazily generated for older routes.
  useEffect(() => {
    setBriefing(route.aiBriefing || null);
    if (route.aiBriefing) return;
    let alive = true;
    ensureRouteBriefing(route.id)
      .then(text => { if (alive && text) setBriefing(text); })
      .catch(() => {});
    return () => { alive = false; };
  }, [route.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Elevation profile for this route (async, non-blocking).
  useEffect(() => {
    const coords = route.geometry?.coordinates;
    if (!coords || coords.length < 3) { setElevation(null); return; }
    const ctl = new AbortController();
    fetchElevationSegments(coords, ctl.signal).then(res => { if (!ctl.signal.aborted) setElevation(res); }).catch(() => {});
    return () => ctl.abort();
  }, [route.id]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  useEffect(() => {
    if (user && !isOwn) {
      getUserRating(route.id, user.uid).then(setUserRating).catch(() => {});
    }
  }, [route.id, user, isOwn]);

  useEffect(() => {
    getUserStats(route.userId).then(setUserStats).catch(() => {});
  }, [route.userId]);

  async function handleRate(stars) {
    if (!user || isOwn || isRating) return;
    setIsRating(true);
    try {
      await rateRoute(route.id, user.uid, stars);
      const wasRated = userRating != null;
      const newCount = wasRated ? ratingCount : ratingCount + 1;
      const oldTotal = (avgRating ?? 0) * ratingCount;
      const newTotal = oldTotal - (userRating ?? 0) + stars;
      setUserRating(stars);
      setRatingCount(newCount);
      setAvgRating(newTotal / newCount);
    } catch (err) {
      showToast('Could not save rating: ' + err.message, 'error');
    } finally {
      setIsRating(false);
    }
  }

  function handleLoad() {
    dispatch({
      type: 'LOAD_ROUTE',
      payload: {
        id: `community_${route.id}`,
        name: route.name,
        waypoints: route.waypoints.map(wp => ({
          lat: wp.lat, lng: wp.lng, name: wp.name || '', isLoopVia: false, isLoopEnd: false,
        })),
        geometry: route.geometry ?? null,
        distance: route.distance,
        duration: route.duration,
        communityMeta: {
          id:            route.id,
          userId:        route.userId,
          name:          route.name,
          description:   route.description || '',
          imageUrls:     route.imageUrls ?? [],
          videoUrls:     route.videoUrls ?? [],
          userName:      route.userName,
          userPhoto:     route.userPhoto ?? null,
          averageRating: route.averageRating ?? null,
          ratingCount:   route.ratingCount ?? 0,
        },
      },
    });
    showToast(`Loaded "${route.name}"`, 'info');
    onClose();
  }

  async function handleShare() {
    const shareRoute = { name: route.name, waypoints: route.waypoints, geometry: route.geometry };
    const url = buildShareURL(shareRoute);
    try {
      if (navigator.share) {
        await navigator.share({ title: route.name, url });
      } else {
        await copyToClipboard(url);
        showToast('Link copied to clipboard', 'info');
      }
    } catch {}
  }

  async function handleExport() {
    await downloadGPX(
      { name: route.name, waypoints: route.waypoints, geometry: route.geometry, distance: route.distance, duration: route.duration },
      { rich: isSubscribed }
    );
    showToast(
      isSubscribed ? 'GPX downloaded — elevation included' : 'GPX downloaded · Pro bakes in elevation',
      'success'
    );
  }

  const [cardBusy, setCardBusy] = useState(false);
  async function handleShareImage() {
    if (cardBusy) return;
    setCardBusy(true);
    try {
      const blob = await generateRouteCard({
        name: route.name, geometry: route.geometry,
        distance: route.distance, duration: route.duration,
        url: `${window.location.origin}/route/${route.id}`,
      });
      if (!blob) throw new Error('no image');
      const fname = (route.name || 'roamr-route').replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '.png';
      const file = new File([blob], fname, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: route.name, text: 'Check out this ride on Motoroam' });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fname; a.click();
        URL.revokeObjectURL(url);
        showToast('Route card downloaded', 'success');
      }
    } catch {
      showToast('Could not create image', 'error');
    } finally {
      setCardBusy(false);
    }
  }

  async function handleUnpublish() {
    setUnpublishing(true);
    try {
      await unpublishRoute(route.id);
      onUnpublished(route.id);
      onClose();
    } catch (err) {
      showToast('Failed to unpublish: ' + err.message, 'error');
      setUnpublishing(false);
    }
  }

  const images = route.imageUrls ?? [];
  const videos = route.videoUrls ?? [];
  const stops = (route.waypoints ?? []).filter(wp => !wp.isLoopEnd && wp.lat != null);
  const [lbIndex, setLbIndex] = useState(-1);
  // Photos first, then YouTube videos — one swipeable strip.
  const mediaItems = [
    ...images.map((src, i) => ({ type: 'image', src, imageIndex: i })),
    ...videos.map(url => ({ id: youTubeId(url), url })).filter(v => v.id).map(v => ({
      type: 'video',
      url: `https://www.youtube.com/watch?v=${v.id}`,
      thumb: `https://img.youtube.com/vi/${v.id}/mqdefault.jpg`,
      label: 'Watch on YouTube',
    })),
  ];
  const stopCount = stops.length;

  return (<>
    {profileTarget && (
      <Suspense fallback={null}>
        <UserProfilePanel
          uid={profileTarget.uid}
          fallbackName={profileTarget.name}
          fallbackPhoto={profileTarget.photo}
          zIndex={zIndex + 10}
          onClose={() => setProfileTarget(null)}
        />
      </Suspense>
    )}
    {createPortal(
    <div className="rdm-overlay" ref={overlayRef} style={{ zIndex }} onClick={e => e.target === overlayRef.current && onClose()}>
      <div className="rdm">

        {/* ── Header ── */}
        <div className="rdm__header">
          <div className="rdm__title-block">
            <h2 className="rdm__title">{route.name}</h2>
          </div>
          <button className="rdm__close" onClick={onClose} aria-label="Close">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M1 1l10 10M11 1L1 11"/>
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="rdm__body">

          {/* Author profile (clickable) + rating */}
          <div className="rdm__profile">
            <button
              className="rdm__profile-main"
              onClick={() => setProfileTarget({ uid: route.userId, name: route.userName, photo: route.userPhoto })}
            >
              {route.userPhoto
                ? <img src={route.userPhoto} className="rdm__profile-avatar" alt="" />
                : <span className="rdm__profile-avatar rdm__profile-avatar--initial">{route.userName?.[0]?.toUpperCase() ?? '?'}</span>}
              <span className="rdm__profile-info">
                <span className="rdm__profile-name">{route.userName}</span>
                {userStats && (
                  <span className="rdm__profile-stats">
                    {userStats.totalRoutes} {userStats.totalRoutes === 1 ? 'route' : 'routes'}
                    {userStats.avgRating != null && <> · <span className="rdm__profile-star">★</span> {userStats.avgRating.toFixed(1)} avg</>}
                  </span>
                )}
              </span>
              <svg className="rdm__profile-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            {authorProfile && (authorProfile.isBusiness || authorProfile.links?.length > 0) && (
              <div className="rdm__profile-links"><ProfileLinks profile={authorProfile} hideBusiness /></div>
            )}
          </div>

          <BusinessOrganizerCard business={route.business} />

          <div className="rdm__rating-section">
            <div className="rdm__rating-avg">
              {[1,2,3,4,5].map(s => (
                <span key={s} className={`rdm__star-display${s <= Math.round(avgRating ?? 0) ? ' rdm__star-display--on' : ''}`}>★</span>
              ))}
              {avgRating != null
                ? <span className="rdm__rating-label">{avgRating.toFixed(1)} · {ratingCount} {ratingCount === 1 ? 'rating' : 'ratings'}</span>
                : <span className="rdm__rating-label rdm__rating-label--none">No ratings yet</span>
              }
            </div>
            {user && !isOwn && (
              <div className="rdm__rating-input">
                <span className="rdm__rating-prompt">{userRating ? 'Your rating:' : 'Rate this route:'}</span>
                <div className="rdm__stars">
                  {[1,2,3,4,5].map(s => (
                    <button
                      key={s}
                      className={`rdm__star${s <= (hoverStar || userRating || 0) ? ' rdm__star--on' : ''}`}
                      onMouseEnter={() => setHoverStar(s)}
                      onMouseLeave={() => setHoverStar(0)}
                      onClick={() => handleRate(s)}
                      disabled={isRating}
                      aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
                    >★</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Load Route CTA */}
          <button className="rdm__load-btn" onClick={handleLoad}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="3 11 22 2 13 21 11 13 3 11"/>
            </svg>
            Load Route
          </button>

          {/* Download GPX — no need to load the route first */}
          <button className="rdm__download-btn" onClick={handleExport}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download GPX
          </button>

          {(route.startName || route.country) && (
            <div className="rdm__location">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
              {[route.startName, route.region, route.country].filter(Boolean).join(' · ')}
            </div>
          )}

          {/* Stats grid */}
          <div className="rdm__stats-grid">
            <div className="rdm__stat-card">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 14 8 14s8-8.75 8-14a8 8 0 0 0-8-8z"/></svg>
              <span className="rdm__stat-value">{route.distance != null ? formatDistance(route.distance) : '—'}</span>
              <span className="rdm__stat-label">DIST.</span>
            </div>
            <div className="rdm__stat-card">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              <span className="rdm__stat-value">{route.duration != null ? formatDuration(route.duration) : '—'}</span>
              <span className="rdm__stat-label">TIME</span>
            </div>
            <div className="rdm__stat-card">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span className="rdm__stat-value">{stopCount}</span>
              <span className="rdm__stat-label">STOPS</span>
            </div>
          </div>

          {route.geometry && (
            <div className="rdm__curviness">
              <span className="rdm__curviness-label">Curviness</span>
              <CurvinessBadge geometry={route.geometry} variant="chip" />
            </div>
          )}

          <RoadBreakdown geometry={route.geometry} />

          {elevation && <ElevationGraph elevation={elevation} />}

          {/* Description */}
          {route.description && (
            <div
              className="rdm__desc rdm__desc--rich"
              dangerouslySetInnerHTML={{ __html: route.description }}
            />
          )}

          {/* Ride briefing (auto-generated from route data) */}
          {briefing && (
            <div className="rdm__briefing">
              <h3 className="rdm__section-heading">RIDE BRIEFING</h3>
              <p className="rdm__briefing-text">{briefing}</p>
            </div>
          )}

          {/* Route Highlights */}
          {(images.length > 0 || videos.length > 0) && (
            <div className="rdm__highlights">
              <h3 className="rdm__section-heading">ROUTE HIGHLIGHTS</h3>

              <MediaCarousel
                className="mc--rounded"
                items={mediaItems}
                onImageClick={(it) => setLbIndex(it.imageIndex)}
              />
            </div>
          )}

          {/* Lightbox */}
          <Lightbox
            open={lbIndex >= 0}
            index={lbIndex}
            close={() => setLbIndex(-1)}
            slides={images.map(src => ({ src }))}
            plugins={[Zoom, Counter]}
            zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
            counter={{ container: { style: { top: 0, bottom: 'unset' } } }}
            styles={{ container: { backgroundColor: 'rgba(0,0,0,0.92)' } }}
          />

          {/* Itinerary */}
          {stops.length > 0 && (
            <div className="rdm__itinerary">
              <h3 className="rdm__section-heading">ITINERARY</h3>
              <div className="rdm__stops">
                {stops.map((wp, i) => {
                  const color = STOP_COLORS[i % STOP_COLORS.length];
                  const label = STOP_LABELS[i] ?? String(i + 1);
                  const meta = i === 0 ? 'Start Point' : i === stops.length - 1 ? 'End Point' : `Stop ${i + 1}`;
                  return (
                    <div key={i} className="rdm__stop">
                      <span className="rdm__stop-badge" style={{ background: color }}>{label}</span>
                      <div className="rdm__stop-info">
                        <span className="rdm__stop-name">{wp.name || `Stop ${label}`}</span>
                        <span className="rdm__stop-meta">{meta}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}


          {/* Embed — only meaningful for the creator promoting their own
              route; a viewer has no site to embed it on. */}
          {isOwn && (
            <div className="rdm__embed-section">
              <button className="rdm__embed-toggle" onClick={() => setShowEmbed(v => !v)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
                Embed this route on your site
                <svg className={`rdm__embed-chevron${showEmbed ? ' rdm__embed-chevron--open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {showEmbed && <EmbedBuilder kind="route" id={route.id} />}
            </div>
          )}

        </div>

        {/* ── Sticky footer ── */}
        <div className="rdm__footer">
          <button className="rdm__footer-btn" onClick={handleShare}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            Share
          </button>
          <button className="rdm__footer-btn" onClick={handleShareImage} disabled={cardBusy}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.5-3.5L9 20"/>
            </svg>
            {cardBusy ? '…' : 'Image'}
          </button>
          <button className="rdm__footer-btn" onClick={handleExport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export
          </button>
          {isOwn && (
            <button className="rdm__footer-btn rdm__footer-btn--danger" onClick={handleUnpublish} disabled={unpublishing}>
              {unpublishing ? '…' : 'Unpublish'}
            </button>
          )}
        </div>

      </div>
    </div>,
    document.body
    )}
  </>);
}
